import { useState, useEffect, useCallback, useRef } from 'react';
import type { AuditLogEntry, AuditFilters } from '@yawi/types';
import { useServices } from '@/app/providers';

interface PaginationState {
  page: number;
  limit: number;
}

interface UseAuditLogsReturn {
  logs: AuditLogEntry[];
  isLoading: boolean;
  error: string | null;
  /** dateFrom/dateTo como los da `<input type="date">` (YYYY-MM-DD) */
  filters: AuditFilters;
  pagination: PaginationState;
  /** Total del conjunto filtrado (no la longitud de la página). */
  total: number;
  totalPages: number;
  setFilters: (filters: AuditFilters) => void;
  setPagination: (pagination: PaginationState) => void;
  refresh: () => void;
  /** Staff de Hommi, para el filtro y para mostrar nombres en la tabla */
  actors: { id: string; name: string }[];
}

/** Día local completo: el input date da YYYY-MM-DD y el backend espera ISO. */
function toQuery(filters: AuditFilters): AuditFilters {
  return {
    ...filters,
    dateFrom: filters.dateFrom && new Date(`${filters.dateFrom}T00:00:00`).toISOString(),
    dateTo: filters.dateTo && new Date(`${filters.dateTo}T23:59:59.999`).toISOString(),
  };
}

export function useAuditLogs(): UseAuditLogsReturn {
  const { auditRepository, adminUserRepository } = useServices();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actors, setActors] = useState<{ id: string; name: string }[]>([]);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [pagination, setPagination] = useState<PaginationState>({ page: 1, limit: 20 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Solo la última petición escribe el estado: una respuesta lenta de un
  // filtro anterior no puede pisar la del filtro actual.
  const requestIdRef = useRef(0);

  const fetchLogs = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await auditRepository.list(toQuery(filters), pagination.page, pagination.limit);
      if (requestId !== requestIdRef.current) return;
      setLogs(result.data);
      setTotal(result.meta.total);
      setTotalPages(result.meta.totalPages);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
      setLogs([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [auditRepository, filters, pagination]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    adminUserRepository
      .findAll()
      .then((admins) => setActors(admins.map((a) => ({ id: a.id, name: a.name || a.email }))))
      .catch(() => setActors([]));
  }, [adminUserRepository]);

  return {
    logs,
    isLoading,
    error,
    filters,
    pagination,
    total,
    totalPages,
    setFilters,
    setPagination,
    refresh: fetchLogs,
    actors,
  };
}
